import {
  Button,
  Callout,
  FormGroup,
  HTMLSelect,
  InputGroup,
} from "@blueprintjs/core";
import React, { useEffect, useState, type ReactNode } from "react";
import { useApiKeys } from "../../../hooks/useApiKeys";
import type { ApiKeyConfig } from "../../../types/apiKeys";
import { TokenList } from "./TokenList";
import { SettingsFormSection } from "./SettingsFormSection";

interface ApiKeyFormProps {
  value: ApiKeyConfig;
  onChange: (next: ApiKeyConfig) => void;
}

export const ApiKeyForm: React.FC<ApiKeyFormProps> = ({ value, onChange }) => {
  const { roles, loadRoles } = useApiKeys();
  const [ipInput, setIpInput] = useState("");

  const update = (field: keyof ApiKeyConfig, val: unknown) => {
    onChange({ ...value, [field]: val });
  };

  const addManyToArray = (field: "roles" | "allowedIps", raw: string) => {
    if (!raw.trim()) return;

    const current = (value[field] as string[]) ?? [];
    const uniqueNewItems = Array.from(
      new Set(
        raw
          .split(";")
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    ).filter((item) => !current.includes(item));

    if (uniqueNewItems.length === 0) return;

    update(field, [...current, ...uniqueNewItems]);
  };

  const removeFromArray = (field: "roles" | "allowedIps", index: number) => {
    const updated = ((value[field] as string[]) ?? []).filter(
      (_, i) => i !== index,
    );
    update(field, updated);
  };

  useEffect(() => {
    loadRoles();
  }, []);

  return (
    <div className="focusoc-form-shell max-h-[72vh] w-[640px] max-w-[calc(100vw-4rem)] overflow-y-auto pr-1">
      <div className="flex flex-col gap-4">
        <SettingsFormSection eyebrow="Identity" title="API key">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px]">
            <FormGroup label="Name" labelFor="api-key-name-input">
              <InputGroup
                fill
                id="api-key-name-input"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update("name", e.target.value)
                }
                placeholder="API key name"
                value={value.name || ""}
              />
            </FormGroup>

            {typeof value.expirationDays !== "undefined"  && (
              <FormGroup
                label="Expiration days"
                labelFor="expiration-days-input"
              >
                <InputGroup
                  id="expiration-days-input"
                  max={90}
                  min={1}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    update(
                      "expirationDays",
                      Math.min(
                        90,
                        Math.max(1, parseInt(e.target.value, 10) || 1),
                      ),
                    )
                  }
                  type="number"
                  value={(value.expirationDays).toString()}
                />
              </FormGroup>
            )}
          </div>
        </SettingsFormSection>

        <SettingsFormSection eyebrow="Access control" title="Scope">
          <div className="grid gap-4 md:grid-cols-2">
            <FormGroup label="Roles">
              <HTMLSelect
                className="w-full"
                fill
                onChange={(e) => {
                  const selected = e.target.value;
                  if (selected && !(value.roles ?? []).includes(selected)) {
                    update("roles", [...(value.roles ?? []), selected]);
                  }
                }}
                options={[
                  { label: "- Select role -", value: "", disabled: true },
                  ...(roles ?? []).map((role) => ({
                    label: role,
                    value: role,
                  })),
                ]}
                value=""
              />

              <TokenList
                items={value.roles ?? []}
                onRemove={(index) => removeFromArray("roles", index)}
              />
            </FormGroup>

            <FormGroup label="Allowed IPs" labelFor="allowed-ips-input">
              <div className="flex gap-2">
                <InputGroup
                  fill
                  id="allowed-ips-input"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIpInput(e.target.value)
                  }
                  placeholder="192.168.1.1;10.0.0.1"
                  value={ipInput}
                />
                <Button
                  intent="primary"
                  onClick={() => {
                    addManyToArray("allowedIps", ipInput);
                    setIpInput("");
                  }}
                  text="Add"
                />
              </div>

              <TokenList
                items={value.allowedIps ?? []}
                onRemove={(index) => removeFromArray("allowedIps", index)}
              />

              {(value.allowedIps ?? []).length === 0 && (
                <Callout
                  className="mt-3 rounded-xl border border-amber-200 bg-amber-50"
                  intent="warning"
                >
                  No allowed IPs defined.
                </Callout>
              )}
            </FormGroup>
          </div>
        </SettingsFormSection>
      </div>
    </div>
  );
};
